import { useMemo, useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Award,
  UploadCloud,
  ExternalLink,
  Calendar,
  Building,
  Loader2,
  Image as ImageIcon
} from "lucide-react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { BACKEND_URL } from "@/config";

const blank = {
  certificationName: "",
  issuingOrganization: "",
  issueDate: "",
  expirationDate: "",
  doesNotExpire: false,
  credentialId: "",
  credentialUrl: "",
  certificateImageUrl: "",
  description: "",
  skills: "",
  displayOrder: 0
};

export default function Certifications() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const fileInputRef = useRef(null);

  const fetchItems = async () => {
    try {
      const res = await api.get('/Certification');
      if (res.success && res.data) {
        const mapped = res.data.map(d => ({
          id: d.id,
          certificationName: d.certificationName || "",
          issuingOrganization: d.issuingOrganization || "",
          issueDate: d.issueDate ? d.issueDate.split("T")[0] : "",
          expirationDate: d.expirationDate ? d.expirationDate.split("T")[0] : "",
          doesNotExpire: d.doesNotExpire || false,
          credentialId: d.credentialId || "",
          credentialUrl: d.credentialUrl || "",
          certificateImageUrl: d.certificateImageUrl || "",
          description: d.description || "",
          skills: d.skills || "",
          displayOrder: d.displayOrder || 0
        }));
        setItems(mapped.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
      }
    } catch (err) {
      toast.error("Failed to load Certifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
      (e) =>
        e.certificationName.toLowerCase().includes(q) ||
        e.issuingOrganization.toLowerCase().includes(q) ||
        e.skills.toLowerCase().includes(q)
    );
  }, [items, query]);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/Certification/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success && res.data) {
        setForm(prev => ({ ...prev, certificateImageUrl: res.data }));
        toast.success("Image uploaded successfully.");
      } else {
        toast.error(res.message || "Failed to upload image.");
      }
    } catch (err) {
      toast.error("An error occurred during upload.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const save = async () => {
    if (!form.certificationName || !form.issuingOrganization) {
      toast.error("Name and Issuing Organization are required.");
      return;
    }
    
    const payload = {
      ...form,
      issueDate: form.issueDate ? form.issueDate : null,
      expirationDate: form.doesNotExpire ? null : (form.expirationDate ? form.expirationDate : null)
    };

    try {
      if (editing) {
        const res = await api.put(`/Certification/${editing.id}`, payload);
        if (res.success) {
          toast.success("Certification updated.");
          fetchItems();
        }
      } else {
        const res = await api.post('/Certification', payload);
        if (res.success) {
          toast.success("Certification added.");
          fetchItems();
        }
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to save record.");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await api.delete(`/Certification/${toDelete.id}`);
      if (res.success) {
        toast.success("Certification removed.");
        fetchItems();
      }
    } catch (err) {
      toast.error("Failed to delete record.");
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Certifications"
        description="Showcase your professional certifications and achievements."
        testId="certifications"
        action={openCreate}
        actionLabel="Add Certification"
        actionIcon={Plus}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            data-testid="cert-search"
            placeholder="Search certifications, organizations, skills…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10 shadow-sm transition-all focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <Badge variant="secondary" className="font-mono text-[10px] w-fit px-3 py-1 bg-primary/10 text-primary">
          {filtered.length} {filtered.length === 1 ? "certificate" : "certificates"}
        </Badge>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed border-2 bg-muted/20">
          <EmptyState
            icon={Award}
            title="No certifications found"
            description="Add your first certification to highlight your expertise."
            actionLabel="Add Certification"
            onAction={openCreate}
            testId="cert-empty"
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filtered.map((row) => (
              <motion.div
                key={row.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="group relative overflow-hidden flex flex-col h-full border hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 bg-gradient-to-b from-card to-card/50">
                  {row.certificateImageUrl ? (
                    <div className="h-40 w-full relative overflow-hidden bg-muted">
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-center">
                        {row.credentialUrl && (
                          <a href={row.credentialUrl} target="_blank" rel="noreferrer" className="text-white hover:text-primary transition-colors p-2 bg-black/50 rounded-full backdrop-blur-sm">
                            <ExternalLink className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                      <img 
                        src={row.certificateImageUrl?.startsWith('http') ? row.certificateImageUrl : `${BACKEND_URL}${row.certificateImageUrl}`} 
                        alt={row.certificationName} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='1' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526'/%3E%3Ccircle cx='12' cy='8' r='6'/%3E%3C/svg%3E";
                          e.target.className = "w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-500 opacity-20";
                        }}
                      />
                    </div>
                  ) : (
                    <div className="h-40 w-full bg-gradient-to-br from-primary/10 to-primary/5 flex flex-col items-center justify-center text-primary/60">
                      <Award className="h-12 w-12 mb-2 opacity-50" strokeWidth={1} />
                      <span className="text-xs font-medium uppercase tracking-wider">No Image</span>
                    </div>
                  )}
                  
                  <CardContent className="p-5 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors" title={row.certificationName}>
                          {row.certificationName}
                        </h3>
                        <div className="flex items-center text-sm text-muted-foreground mt-1">
                          <Building className="h-3.5 w-3.5 mr-1.5 opacity-70" />
                          <span className="line-clamp-1">{row.issuingOrganization}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 font-mono bg-muted/30 p-2 rounded-md">
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {row.issueDate ? new Date(row.issueDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : 'N/A'}
                      </div>
                      {!row.doesNotExpire && row.expirationDate && (
                        <>
                          <span className="text-border">-</span>
                          <div className="flex items-center">
                            {new Date(row.expirationDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}
                          </div>
                        </>
                      )}
                      {row.doesNotExpire && (
                        <>
                          <span className="text-border">-</span>
                          <div className="flex items-center text-green-600 dark:text-green-400">
                            No Expiration
                          </div>
                        </>
                      )}
                    </div>
                    
                    {row.description && (
                      <p className="text-sm text-muted-foreground/80 line-clamp-2 mb-4 flex-1">
                        {row.description}
                      </p>
                    )}
                    
                    {row.skills && (
                      <div className="flex flex-wrap gap-1.5 mt-auto mb-4">
                        {row.skills.split(',').map((skill, i) => (
                          <Badge key={i} variant="outline" className="text-[9px] font-mono bg-background">
                            {skill.trim()}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border/50 mt-auto">
                      <div className="text-xs font-mono text-muted-foreground/50">
                        {row.credentialId && `ID: ${row.credentialId}`}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setToDelete(row);
                            setConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="cert-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Certification" : "Add Certification"}
            </DialogTitle>
            <DialogDescription>
              Details about your certification, licenses, or professional credentials.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-x-6 gap-y-5 py-2">
            <div className="col-span-2 flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-xl bg-muted/10 hover:bg-muted/20 transition-colors relative group">
              {form.certificateImageUrl ? (
                <div className="relative w-full max-w-sm aspect-[4/3] rounded-md overflow-hidden bg-muted">
                  <img src={form.certificateImageUrl?.startsWith('http') ? form.certificateImageUrl : `${BACKEND_URL}${form.certificateImageUrl}`} alt="Certificate" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadCloud className="h-4 w-4 mr-2" />}
                      Change Image
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="h-16 w-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-3">
                    {uploading ? <Loader2 className="h-8 w-8 animate-spin" /> : <ImageIcon className="h-8 w-8" />}
                  </div>
                  <h4 className="text-sm font-semibold mb-1">Upload Certificate Image</h4>
                  <p className="text-xs text-muted-foreground mb-4">PNG, JPG or GIF (max 5MB)</p>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                    Browse Files
                  </Button>
                </div>
              )}
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*" 
                onChange={handleImageUpload} 
              />
            </div>

            <Field label="Certification Name" span={2} required>
              <Input
                value={form.certificationName}
                onChange={(e) => setForm({ ...form, certificationName: e.target.value })}
                placeholder="AWS Certified Solutions Architect"
                className="bg-background"
              />
            </Field>
            
            <Field label="Issuing Organization" span={2} required>
              <Input
                value={form.issuingOrganization}
                onChange={(e) => setForm({ ...form, issuingOrganization: e.target.value })}
                placeholder="Amazon Web Services"
                className="bg-background"
              />
            </Field>
            
            <Field label="Issue Date">
              <Input
                type="date"
                value={form.issueDate}
                onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                className="bg-background"
              />
            </Field>
            
            <div className="col-span-2 sm:col-span-1 flex flex-col">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Expiration Date
                </Label>
                <div className="flex items-center space-x-2">
                  <Label htmlFor="doesNotExpire" className="text-[10px] font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    No Expiration
                  </Label>
                  <Switch 
                    id="doesNotExpire"
                    checked={form.doesNotExpire}
                    onCheckedChange={(checked) => setForm({ ...form, doesNotExpire: checked, expirationDate: checked ? "" : form.expirationDate })}
                    className="scale-75"
                  />
                </div>
              </div>
              <Input
                type="date"
                value={form.expirationDate}
                onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
                disabled={form.doesNotExpire}
                className="bg-background disabled:opacity-50 disabled:bg-muted"
              />
            </div>

            <Field label="Credential ID">
              <Input
                value={form.credentialId}
                onChange={(e) => setForm({ ...form, credentialId: e.target.value })}
                placeholder="ABC123XYZ"
                className="bg-background font-mono text-sm"
              />
            </Field>
            
            <Field label="Credential URL">
              <Input
                type="url"
                value={form.credentialUrl}
                onChange={(e) => setForm({ ...form, credentialUrl: e.target.value })}
                placeholder="https://..."
                className="bg-background"
              />
            </Field>

            <Field label="Skills Covered" span={2}>
              <Input
                value={form.skills}
                onChange={(e) => setForm({ ...form, skills: e.target.value })}
                placeholder="e.g. Cloud Architecture, React, Node.js (comma separated)"
                className="bg-background"
              />
            </Field>

            <Field label="Description" span={2}>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of the certification and what it entails..."
                rows={3}
                className="bg-background resize-none"
              />
            </Field>
          </div>
          
          <DialogFooter className="pt-4 border-t border-border/50">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} className="min-w-32 shadow-md hover:shadow-lg transition-all">
              {editing ? "Save changes" : "Add Certification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete certification?"
        description="This will permanently remove the certification from your portfolio. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const Field = ({ label, span = 1, required = false, children }) => (
  <div className={span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
      {label}
      {required && <span className="text-destructive">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
